import React from 'react';
import { HashRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Navigation from './components/Navigation';
import HomePage from './components/HomePage';
import ReservationPage from './components/ReservationPage';
import AdminPage from './components/AdminPage';
import LoginPage from './components/LoginPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import PurchaseTermsPage from './components/PurchaseTermsPage';

import AdminRoute from './components/AdminRoute';
import ProtectedRoute from './components/ProtectedRoute';
// eslint-disable-next-line no-unused-vars
import ClientOnlyRoute from './components/ClientOnlyRoute';
import ProfilePage from './components/ProfilePage';
import AdminUsersPage from './components/AdminUsersPage';
import AdminGroupTrainingPage from './components/AdminGroupTrainingPage';
import AdminGroupFormavimasPage from './components/AdminGroupFormavimasPage';
import CoachNuolatiniaiLaikaiPage from './components/CoachNuolatiniaiLaikaiPage';
import AdminTreneriaiPage from './components/AdminTreneriaiPage';
import AdminTreneriuNuolatiniaiLaikaiPage from './components/AdminTreneriuNuolatiniaiLaikaiPage';
import AdminStatistikaPage from './components/AdminStatistikaPage';
import AdminWhenUsersBookPage from './components/AdminWhenUsersBookPage';
import AdminCourtPricesPage from './components/AdminCourtPricesPage';
import AdminMailsPage from './components/AdminMailsPage';
import CoachClientsPage from './components/CoachClientsPage';
import GroupTrainingPage from './components/GroupTrainingPage';
import CoachesInvitePage from './components/CoachesInvitePage';
import CoachRegistrationForm from './components/CoachRegistrationForm';
import DeleteAccountPage from './components/DeleteAccountPage';
import ClientVisosRezervacijosPage from './components/ClientVisosRezervacijosPage';
import ProformaInvoiceTestPage from './components/ProformaInvoiceTestPage';
import InvoiceFacturaTestPage from './components/InvoiceFacturaTestPage';
import InvoiceCoachMBTestPage from './components/InvoiceCoachMBTestPage';
import AdminSaskaitosPage from './components/AdminSaskaitosPage';
import AdminApskaitaPage from './components/AdminApskaitaPage';
import AdminKlientuBalansasPage from './components/AdminKlientuBalansasPage';
import ClientSaskaitosPage from './components/ClientSaskaitosPage';
import { AuthProvider } from './contexts/AuthContext';
import './App.css';

// Create a custom theme with Lithuanian tennis trainer colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#48bb78', // Green color from the original design
      dark: '#38a169',
      light: '#68d391',
    },
    secondary: {
      main: '#4a5568', // Gray color from the original design
      dark: '#2d3748',
      light: '#718096',
    },
    background: {
      default: '#f7fafc',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <div className="App">
            <Navigation />
            <Box id="site-main" component="main" sx={{ flex: 1 }}>
              <Routes>
                <Route path="/" element={<HomePage />} />
              <Route path="/rezervacija" element={
                <ClientOnlyRoute>
                  <ProtectedRoute requireAuth={false} requireNonAnonymous={false}>
                    <ReservationPage />
                  </ProtectedRoute>
                </ClientOnlyRoute>
              } />
              <Route path="/rezervacija/:coachSlug" element={
                <ClientOnlyRoute>
                  <ProtectedRoute requireAuth={false} requireNonAnonymous={false}>
                    <ReservationPage />
                  </ProtectedRoute>
                </ClientOnlyRoute>
              } />
              <Route path="/rezervacija/grupines" element={
                <ClientOnlyRoute>
                  <ProtectedRoute requireAuth={false} requireNonAnonymous={false}>
                    <GroupTrainingPage />
                  </ProtectedRoute>
                </ClientOnlyRoute>
              } />
              <Route path="/visos-rezervacijos" element={
                <ProtectedRoute requireAuth={true} requireNonAnonymous={false}>
                  <ClientVisosRezervacijosPage />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              } />
              <Route path="/admin/nuolatiniai-laikai" element={
                <AdminRoute>
                  <CoachNuolatiniaiLaikaiPage />
                </AdminRoute>
              } />
              <Route path="/admin/visos-rezervacijos" element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              } />
              <Route path="/admin/treneriu-nuolatiniai-laikai" element={
                <AdminRoute>
                  <AdminTreneriuNuolatiniaiLaikaiPage />
                </AdminRoute>
              } />
              <Route path="/admin/administracija" element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              } />
              <Route path="/admin/statistika" element={
                <AdminRoute>
                  <AdminStatistikaPage />
                </AdminRoute>
              } />
              <Route path="/admin/when-users-book" element={
                <AdminRoute>
                  <AdminWhenUsersBookPage />
                </AdminRoute>
              } />
              <Route path="/admin/aiksteliu-kainos" element={
                <AdminRoute>
                  <AdminCourtPricesPage />
                </AdminRoute>
              } />
              <Route path="/admin/mails" element={
                <AdminRoute>
                  <AdminMailsPage />
                </AdminRoute>
              } />
              <Route path="/admin/saskaitos" element={
                <AdminRoute>
                  <AdminSaskaitosPage />
                </AdminRoute>
              } />
              <Route path="/admin/apskaita" element={
                <AdminRoute>
                  <AdminApskaitaPage />
                </AdminRoute>
              } />
              <Route path="/admin/klientu-balansas" element={
                <AdminRoute>
                  <AdminKlientuBalansasPage />
                </AdminRoute>
              } />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/profilis" element={
                <ProtectedRoute requireAuth={true} requireNonAnonymous={true}>
                  <ProfilePage />
                </ProtectedRoute>
              } />
              <Route path="/saskaitos" element={
                <ProtectedRoute requireAuth={true} requireNonAnonymous={true}>
                  <ClientSaskaitosPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <AdminRoute>
                  <AdminUsersPage />
                </AdminRoute>
              } />
              <Route path="/admin/coach-klientai" element={
                <ProtectedRoute requireAuth={true} requireNonAnonymous={true}>
                  <CoachClientsPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/grupines" element={
                <AdminRoute>
                  <AdminGroupTrainingPage />
                </AdminRoute>
              } />
              <Route path="/admin/grupines/formavimas" element={
                <AdminRoute>
                  <AdminGroupFormavimasPage />
                </AdminRoute>
              } />
              <Route path="/coachesinvite" element={<CoachesInvitePage />} />
              <Route path="/coachregistrationform" element={<CoachRegistrationForm />} />
              <Route path="/admin/treneriai" element={
                <AdminRoute>
                  <AdminTreneriaiPage />
                </AdminRoute>
              } />
              <Route path="/admin/invoices/test-proforma" element={
                <AdminRoute>
                  <ProformaInvoiceTestPage />
                </AdminRoute>
              } />
              <Route path="/admin/invoices/test-factura" element={
                <AdminRoute>
                  <InvoiceFacturaTestPage />
                </AdminRoute>
              } />
              <Route path="/admin/invoices/test-coach-mb" element={
                <AdminRoute>
                  <InvoiceCoachMBTestPage />
                </AdminRoute>
              } />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/pirkimo-taisykles" element={<PurchaseTermsPage />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              </Routes>
            </Box>
            <Box
              id="site-footer"
              component="footer"
              sx={{
                py: 2,
                px: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: { xs: 1.5, sm: 2 },
                  columnGap: 2,
                  rowGap: 1,
                }}
              >
                <Typography
                  component={Link}
                  to="/privacy"
                  variant="body2"
                  color="text.secondary"
                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, textAlign: 'center' }}
                >
                  Privatumo politika
                </Typography>
                <Typography component="span" variant="body2" color="text.disabled" sx={{ display: { xs: 'none', sm: 'block' }, userSelect: 'none' }}>
                  |
                </Typography>
                <Typography
                  component={Link}
                  to="/pirkimo-taisykles"
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                    textAlign: 'center',
                    maxWidth: { xs: '100%', sm: 'none' },
                  }}
                >
                  Pirkimo taisyklės ir pinigų grąžinimo politika
                </Typography>
              </Box>
            </Box>
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
